import React from 'react';
import { motion } from 'framer-motion';

interface SectionTitleProps {
  title: string;
  subtitle?: string;
  description?: string;
  align?: 'left' | 'center' | 'right';
  centered?: boolean;
  className?: string;
  animate?: boolean;
}

/**
 * 节标题组件
 * 用于各个页面区块的标题展示
 */
export const SectionTitle: React.FC<SectionTitleProps> = ({
  title,
  subtitle,
  description,
  align = 'center',
  centered = false,
  className = '',
  animate = true,
}) => {
  // 如果设置了centered，则覆盖align设置
  const finalAlign = centered ? 'center' : align;
  // 对齐样式
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };
  
  // 动画配置
  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut' as const,
      },
    },
  };
  
  const subtitleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        ease: 'easeOut' as const,
      },
    },
  };
  
  const descriptionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.4,
        ease: 'easeOut' as const,
      },
    },
  };
  
  const containerClasses = `${alignClasses[finalAlign]} ${className}`;
  
  if (animate) {
    return (
      <motion.div
        className={containerClasses}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        {subtitle && (
          <motion.p
            variants={subtitleVariants}
            className="text-primary-500 font-semibold text-base uppercase tracking-wider mb-2"
          >
            {subtitle}
          </motion.p>
        )}
        
        <motion.h2
          variants={titleVariants}
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
        >
          {title}
        </motion.h2>
        
        {description && (
          <motion.p
            variants={descriptionVariants}
            className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed"
          >
            {description}
          </motion.p>
        )}
      </motion.div>
    );
  }
  
  return (
    <div className={containerClasses}>
      {subtitle && (
        <p className="text-primary-500 font-semibold text-base uppercase tracking-wider mb-2">
          {subtitle}
        </p>
      )}
      
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
        {title}
      </h2>
      
      {description && (
        <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
          {description}
        </p>
      )}
    </div>
  );
};